// Per-service presets for the API Explorer, keyed by connection service_code.
// Keeping these out of the component so the UI stays declarative and the lists
// are easy to extend.
//
// Phase-2 focus: CWM -> Autotask *configuration* migration. The CWM and
// autotask lists below are organized so a config concept on one side lines up
// with where to read it on the other:
//   CWM setup tables (boards/statuses/types/priorities/SLAs/sources/teams/…)
//   -> AT standalone config entities + AT picklist fields (EntityInformation
//      /Fields + setupField, via the Quick Lookups below).
//
// CWM URI convention: NO leading slash. The CWM client builds
// `.../apis/3.0/<uri>` verbatim, so `service/boards` is correct and
// `/service/boards` produces a double slash. AT URIs keep the leading slash
// (the AT client strips it).

// Quick-reference endpoints — click to fill the URI. A service's value is either
// a flat array of hints, or an array of { group, items } sections (rendered as
// collapsible groups; the first group starts expanded).
export const ENDPOINT_HINTS = {
    'connectwise-cloud': [
        // Pinned (no group) — always-visible high-level objects.
        { uri: 'company/companies', label: 'Companies' },
        { uri: 'company/companies/{id}/sites', label: 'Company Sites' },
        { uri: 'company/contacts', label: 'Contacts' },
        { uri: 'service/tickets', label: 'Tickets' },
        { uri: 'project/projects', label: 'Projects' },
        { group: 'Service Board Setup', items: [
            { uri: 'service/boards', label: 'Boards' },
            { uri: 'service/boards/{id}/statuses', label: 'Board Statuses' },
            { uri: 'service/boards/{id}/types', label: 'Board Types' },
            { uri: 'service/boards/{id}/subtypes', label: 'Board SubTypes' },
            { uri: 'service/boards/{id}/items', label: 'Board Items' },
            { uri: 'service/boards/{id}/typeSubTypeItemAssociations', label: 'TSI Associations' },
            { uri: 'service/boards/{id}/teams', label: 'Board Teams' },
        ] },
        { group: 'Service Lookups', items: [
            { uri: 'service/priorities', label: 'Priorities' },
            { uri: 'service/slas', label: 'SLAs' },
            { uri: 'service/sources', label: 'Sources' },
            { uri: 'service/impacts', label: 'Impacts' },
            { uri: 'service/severities', label: 'Severities' },
        ] },
        { group: 'Configurations', items: [
            { uri: 'company/configurations', label: 'Configurations' },
            { uri: 'company/configurations/types', label: 'Configuration Types' },
            { uri: 'company/configurations/statuses', label: 'Configuration Statuses' },
            { uri: 'company/configurations/types/{id}/questions', label: 'Configuration Questions' },
            { uri: 'company/configurations/types/{typeId}/questions/{questionId}/values', label: 'Question Values' },
        ] },
        { group: 'Company / Org', items: [
            { uri: 'company/companies/statuses', label: 'Company Statuses' },
            { uri: 'company/companies/types', label: 'Company Types' },
            { uri: 'system/locations', label: 'Locations' },
            { uri: 'system/departments', label: 'Departments' },
        ] },
        { group: 'People / Security', items: [
            { uri: 'system/members', label: 'Members' },
            { uri: 'system/roles', label: 'Security Roles' },
            { uri: 'system/members/types', label: 'Member Types' },
        ] },
    ],
    'connectwise-onprem': [
        { uri: 'service/boards', label: 'Boards' },
        { uri: 'service/boards/{id}/statuses', label: 'Board Statuses' },
        { uri: 'service/boards/{id}/types', label: 'Board Types' },
        { uri: 'service/priorities', label: 'Priorities' },
        { uri: 'service/tickets', label: 'Tickets' },
        { uri: 'company/companies', label: 'Companies' },
        { uri: 'company/contacts', label: 'Contacts' },
    ],
    'freshservice': [
        { uri: '/tickets', label: 'Tickets' },
        { uri: '/tickets/filter', label: 'Tickets (Filter)' },
        { uri: '/service_catalog/items', label: 'Service Items' },
        { uri: '/departments', label: 'Departments' },
        { uri: '/agents', label: 'Agents' },
        { uri: '/requesters', label: 'Requesters' },
        { uri: '/locations', label: 'Locations' },
        { uri: '/groups', label: 'Groups' },
        { uri: '/ticket_fields', label: 'Ticket Fields' },
        { uri: '/assets', label: 'Assets' },
    ],
    'servicenow': [
        { uri: '/table/incident', label: 'Incidents' },
        { uri: '/table/sc_request', label: 'Requests' },
        { uri: '/table/sc_req_item', label: 'Request Items' },
        { uri: '/table/change_request', label: 'Changes' },
        { uri: '/table/problem', label: 'Problems' },
        { uri: '/table/cmdb_ci', label: 'CMDB CIs' },
        { uri: '/table/sys_user', label: 'Users' },
        { uri: '/table/sys_user_group', label: 'Groups' },
        { uri: '/table/core_company', label: 'Companies' },
        { uri: '/table/cmn_location', label: 'Locations' },
    ],
    'halo': [
        { uri: '/tickets', label: 'Tickets' },
        { uri: '/clients', label: 'Clients' },
        { uri: '/users', label: 'Users' },
        { uri: '/agent', label: 'Agents' },
        { uri: '/site', label: 'Sites' },
        { uri: '/status', label: 'Statuses' },
        { uri: '/priority', label: 'Priorities' },
        { uri: '/category', label: 'Categories' },
        { uri: '/team', label: 'Teams' },
        { uri: '/asset', label: 'Assets' },
    ],
    'autotask': [
        // Pinned (no group) — always-visible high-level entities.
        { uri: '/Companies', label: 'Companies' },
        { uri: '/CompanyLocations', label: 'Company Locations' },
        { uri: '/Contacts', label: 'Contacts' },
        { uri: '/Tickets', label: 'Tickets' },
        { uri: '/Projects', label: 'Projects' },
        { group: 'Ticket Setup', items: [
            { uri: '/Resources', label: 'Resources' },
            { uri: '/Roles', label: 'Roles' },
            { uri: '/IssueTypes', label: 'Issue Types' },
            { uri: '/SubIssueTypes', label: 'Sub Issue Types' },
            { uri: '/TicketCategories', label: 'Ticket Categories' },
            { uri: '/ServiceLevelAgreements', label: 'SLAs' },
            { uri: '/TimeEntries', label: 'Time Entries' },
        ] },
        // Configurations (= CWM Configurations). Category is its own entity (ref, not picklist).
        { group: 'Configurations', items: [
            { uri: '/ConfigurationItems', label: 'Configuration Items' },
            { uri: '/ConfigurationItemCategories', label: 'Configuration Categories' },
            { uri: '/ConfigurationItemTypes', label: 'Configuration Types' },
            { uri: '/ConfigurationItemCategoryUdfAssociations', label: 'Config Category UDFs' },
        ] },
        { group: 'Field Metadata', items: [
            { uri: '/Tickets/EntityInformation/Fields', label: 'Ticket Fields (meta)' },
            { uri: '/Companies/EntityInformation/Fields', label: 'Company Fields (meta)' },
            { uri: '/ConfigurationItems/EntityInformation/Fields', label: 'CI Fields (meta)' },
        ] },
    ],
};

// Composite lookups — click to fill URI + integrationOptions together. For AT
// picklist fields (not standalone entities): hit the entity's metadata endpoint
// and extract the named field's picklist values via setupField.
//
// These are the CWM-setup -> AT-picklist pairings for config migration: the
// label names the CWM concept, the lookup reads the matching AT picklist.
export const COMPOSITE_HINTS = {
    'autotask': [
        { label: 'Ticket Statuses', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'status' } },
        { label: 'Ticket Priorities', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'priority' } },
        { label: 'Ticket Queues (Boards)', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'queueID' } },
        { label: 'Ticket Sources', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'source' } },
        { label: 'Ticket Types', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'ticketType' } },
        { label: 'Ticket Categories', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'ticketCategory' } },
        { label: 'Issue Types', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'issueType' } },
        { label: 'Sub Issue Types', uri: '/Tickets/EntityInformation/Fields', integrationOptions: { setupField: 'subIssueType' } },
        { label: 'Company Types', uri: '/Companies/EntityInformation/Fields', integrationOptions: { setupField: 'companyType' } },
        { label: 'Company Classification', uri: '/Companies/EntityInformation/Fields', integrationOptions: { setupField: 'classification' } },
        { label: 'CI Types', uri: '/ConfigurationItems/EntityInformation/Fields', integrationOptions: { setupField: 'configurationItemType' } },
    ],
};

// Query-param presets — click to add a key/value row.
export const PARAM_HINTS = {
    'connectwise-cloud': [
        { key: 'conditions', value: 'id > 0', label: 'conditions' },
        { key: 'orderBy', value: 'id desc', label: 'orderBy' },
        { key: 'childConditions', value: '', label: 'childConditions' },
        { key: 'customFieldConditions', value: '', label: 'customFieldConditions' },
    ],
    'connectwise-onprem': [
        { key: 'conditions', value: 'id > 0', label: 'conditions' },
        { key: 'orderBy', value: 'id desc', label: 'orderBy' },
    ],
    'freshservice': [
        { key: 'query', value: 'status:2 AND priority:3', label: 'query (filter)' },
        { key: 'filter', value: 'new_and_my_open', label: 'filter (view)' },
        { key: 'order_by', value: 'created_at', label: 'order_by' },
        { key: 'order_type', value: 'desc', label: 'order_type' },
        { key: 'include', value: 'stats', label: 'include' },
        { key: 'updated_since', value: '2024-01-01T00:00:00Z', label: 'updated_since' },
        { key: 'per_page', value: '30', label: 'per_page' },
        { key: 'page', value: '1', label: 'page' },
    ],
    'servicenow': [
        { key: 'sysparm_query', value: 'active=true', label: 'sysparm_query' },
        { key: 'sysparm_fields', value: 'number,short_description,state', label: 'sysparm_fields' },
        { key: 'sysparm_display_value', value: 'true', label: 'display_value' },
        { key: 'sysparm_limit', value: '10', label: 'sysparm_limit' },
        { key: 'sysparm_offset', value: '0', label: 'sysparm_offset' },
    ],
    'halo': [
        { key: 'search', value: '', label: 'search' },
        { key: 'order', value: 'id', label: 'order' },
        { key: 'orderdesc', value: 'true', label: 'orderdesc' },
        { key: 'ticketidonly', value: 'false', label: 'ticketidonly' },
        { key: 'date_search', value: '', label: 'date_search' },
        { key: 'open_only', value: 'true', label: 'open_only' },
    ],
    // Autotask: searchFilter is a single JSON array of {op, field, value} conditions.
    // Only the common presets are clickable here; the full operator set lives in
    // OPERATOR_REFERENCE (copy-paste popover) so clicking doesn't clobber a filter.
    'autotask': [
        { key: 'searchFilter', value: '[{"op":"exist","field":"id"}]', label: 'all' },
        { key: 'searchFilter', value: '[{"op":"eq","field":"isActive","value":true}]', label: 'active only' },
        { key: 'searchFilter', value: '[{"op":"eq","field":"id","value":0}]', label: 'eq' },
        { key: 'searchFilter', value: '[{"op":"contains","field":"companyName","value":"paper"}]', label: 'contains' },
        { key: 'searchFilter', value: '[{"op":"in","field":"id","value":[1,2,3]}]', label: 'in' },
    ],
};

// Full operator reference for the copy-paste popover. Not click-to-apply — each
// row is a snippet you copy into the filter yourself.
export const OPERATOR_REFERENCE = {
    'autotask': {
        label: 'searchFilter operators',
        note: 'Array of conditions (implicit AND). exist/notExist take no value; in/notIn take an array. Nest with and/or + items.',
        operators: [
            { op: 'eq', snippet: '{"op":"eq","field":"id","value":0}', desc: 'equals' },
            { op: 'noteq', snippet: '{"op":"noteq","field":"id","value":0}', desc: 'not equal' },
            { op: 'gt', snippet: '{"op":"gt","field":"id","value":0}', desc: 'greater than' },
            { op: 'gte', snippet: '{"op":"gte","field":"id","value":0}', desc: 'greater or equal' },
            { op: 'lt', snippet: '{"op":"lt","field":"id","value":0}', desc: 'less than' },
            { op: 'lte', snippet: '{"op":"lte","field":"id","value":0}', desc: 'less or equal' },
            { op: 'beginsWith', snippet: '{"op":"beginsWith","field":"companyName","value":"A"}', desc: 'starts with' },
            { op: 'endsWith', snippet: '{"op":"endsWith","field":"companyName","value":"Inc"}', desc: 'ends with' },
            { op: 'contains', snippet: '{"op":"contains","field":"companyName","value":"paper"}', desc: 'substring match' },
            { op: 'exist', snippet: '{"op":"exist","field":"id"}', desc: 'field is not null' },
            { op: 'notExist', snippet: '{"op":"notExist","field":"id"}', desc: 'field is null' },
            { op: 'in', snippet: '{"op":"in","field":"id","value":[1,2,3]}', desc: 'in list' },
            { op: 'notIn', snippet: '{"op":"notIn","field":"id","value":[1,2,3]}', desc: 'not in list' },
            { op: 'and', snippet: '{"op":"and","items":[{"op":"eq","field":"isActive","value":true},{"op":"gt","field":"id","value":0}]}', desc: 'AND group' },
            { op: 'or', snippet: '{"op":"or","items":[{"op":"eq","field":"id","value":1},{"op":"eq","field":"id","value":2}]}', desc: 'OR group' },
        ],
    },
};

// Integration-specific input presets — values for the integrationOptions channel
// (not query args) that steer the abstraction by injecting integration-specific fields.
export const INTEGRATION_INPUT_HINTS = {
    'autotask': [
        { label: 'setupField: status', value: { setupField: 'status' } },
        { label: 'rawPath (verbatim URI)', value: { rawPath: true } },
        { label: 'skipFetch', value: { skipFetch: true } },
    ],
    'freshservice': [
        { label: 'setupField', value: { setupField: 'category' } },
        { label: 'nestedLevel', value: { setupField: 'category', nestedLevel: 2 } },
    ],
};
